import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react'
import type { Session, User } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import type { Profile, Account } from '@limo/database'

type UserRole = 'admin' | 'user'

interface AuthContextType {
  session: Session | null
  user: User | null
  profile: Profile | null
  account: Account | null
  role: UserRole | null
  isLoading: boolean
  isAuthenticated: boolean
  isAdmin: boolean
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>
  signOut: () => Promise<void>
  resetPassword: (email: string) => Promise<{ error: Error | null }>
  updatePassword: (password: string) => Promise<{ error: Error | null }>
  refreshProfile: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [user, setUser] = useState<User | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [account, setAccount] = useState<Account | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const role = profile?.role as UserRole | null
  const isAuthenticated = !!session && !!profile
  const isAdmin = role === 'admin'

  const fetchProfile = useCallback(async (userId: string) => {
    const { data: profileData, error: profileError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .eq('status', 'active')
      .single()

    if (profileError || !profileData) {
      console.error('Error fetching profile:', profileError)
      setProfile(null)
      setAccount(null)
      return
    }

    setProfile(profileData)

    // If user has an account, fetch it
    if (profileData.account_id) {
      const { data: accountData, error: accountError } = await supabase
        .from('accounts')
        .select('*')
        .eq('id', profileData.account_id)
        .eq('status', 'active')
        .single()

      if (accountError || !accountData) {
        console.error('Error fetching account:', accountError)
        setAccount(null)
        return
      }

      setAccount(accountData)
    } else {
      setAccount(null)
    }
  }, [])

  const refreshProfile = useCallback(async () => {
    if (user?.id) {
      await fetchProfile(user.id)
    }
  }, [user?.id, fetchProfile])

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setUser(session?.user ?? null)
      if (session?.user) {
        fetchProfile(session.user.id).finally(() => setIsLoading(false))
      } else {
        setIsLoading(false)
      }
    })

    // Listen for auth changes
    // IMPORTANT: Do NOT use async/await inside onAuthStateChange - it causes the
    // Supabase client to hang after tab switches. Use fire-and-forget pattern instead.
    // See: https://github.com/orgs/supabase/discussions/17612
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session)
      setUser(session?.user ?? null)

      if (event === 'SIGNED_IN' && session?.user) {
        // Fire and forget - don't await
        fetchProfile(session.user.id)
      } else if (event === 'SIGNED_OUT') {
        setProfile(null)
        setAccount(null)
      }
    })

    // Refresh session when tab becomes visible again
    // This handles cases where the token expires while the tab is inactive
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
          if (currentSession) {
            setSession(currentSession)
            setUser(currentSession.user)
          }
        })
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      subscription.unsubscribe()
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [fetchProfile])

  // Fire-and-forget security notification emails (new login, password change).
  // Deliberately not awaited and errors are swallowed — email delivery must
  // never block or fail the auth flow.
  const sendSecurityNotification = (type: 'new_login' | 'password_changed') => {
    void supabase.functions
      .invoke('send-notification-email', {
        body: {
          type,
          context: {
            userAgent: navigator.userAgent,
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
        },
      })
      .catch(() => {})
  }

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    // Trigger from signIn only — NOT onAuthStateChange('SIGNED_IN'), which
    // also fires on token refresh and tab focus. This scopes the security
    // email to real password logins.
    if (!error) {
      sendSecurityNotification('new_login')
    }
    return { error: error as Error | null }
  }

  const signOut = async () => {
    await supabase.auth.signOut()
    setProfile(null)
    setAccount(null)
  }

  const resetPassword = async (email: string) => {
    // Build the redirect URL for the password reset email link. Prefer the
    // current window origin so the user is returned to the same environment
    // they requested the reset from (localhost in dev, production domain in
    // prod). The resulting URL MUST be present in the Supabase project's
    // Auth > URL Configuration > Redirect URLs allowlist, otherwise Supabase
    // will reject the request and the email is never sent.
    const redirectTo = `${window.location.origin}/reset-password`

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo,
    })

    if (error) {
      console.error('supabase.auth.resetPasswordForEmail failed', {
        redirectTo,
        error,
      })
    }

    return { error: error as Error | null }
  }

  const updatePassword = async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password })
    if (!error) {
      sendSecurityNotification('password_changed')
    }
    return { error: error as Error | null }
  }

  const value: AuthContextType = {
    session,
    user,
    profile,
    account,
    role,
    isLoading,
    isAuthenticated,
    isAdmin,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
    refreshProfile,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
